import { Avatar, Body, Heading } from '@assembly-js/design-system';
import { Skeleton } from '@material-ui/lab';
import clsx from 'clsx';
import React, { useMemo } from 'react';

import { RichTextStyles } from 'src/components/tiptap/RichTextStyles';
import { PortalConfig } from 'src/context';
import { PoweredByAssemblyBadge } from 'src/features/badge/PoweredByAssemblyBadge';
import { BrandingHeader } from 'src/routes/payments/Checkout/InvoiceContext/components/BrandingHeader';
import { CheckoutPaymentLink } from 'src/routes/payments/payment-links/components/PaymentLinkPreview';
import { Currencies } from 'src/services/api/invoicesApi';
import { StripeCurrencyFormatterAdapter } from 'src/utils/PaymentsUtils';
import { formatBillingInterval } from 'src/utils/SubscriptionUtils';

type Props = {
  target: CheckoutPaymentLink;
  portalConfig: PortalConfig;
  isPreview?: boolean;
  isActualCheckout: boolean;
  onExit: () => void;
  isLoggedInUser?: boolean;
};

export const PaymentLinkCheckoutDetail: React.FC<Props> = ({
  target,
  portalConfig,
  isPreview = false,
  isActualCheckout,
  onExit,
  isLoggedInUser,
}) => {
  const logo = portalConfig.assets.logo.iconImageUrl;
  const [isImageLoading, setImageLoadingStatus] = React.useState(true);
  const portalName = portalConfig.name;
  const {
    name,
    lineItems,
    amountDue,
    description,
    coverUrl,
    interval,
    currency,
    fees,
  } = target;

  const totalLineItemSummary = useMemo(() => {
    return lineItems?.reduce(
      (acc, item) => {
        acc.totalQuantity += Number(item.quantity);
        acc.totalAmount += Number(item.quantity);
        return acc;
      },
      { totalQuantity: 0, totalAmount: 0 },
    );
  }, [lineItems]);

  /**
   * This useEffect is used to set the image loading status to true when the coverUrl is changed.
   */
  React.useEffect(() => {
    if (coverUrl) {
      setImageLoadingStatus(true);
    } else {
      setImageLoadingStatus(false);
    }
  }, [coverUrl]);
  const [isDescriptionLong, setisDescriptionLong] = React.useState(false);

  const descriptionRef = React.useRef<HTMLDivElement>(null);

  // Function to check the description height and determine if it's "long"
  const checkDescriptionHeight = () => {
    if (descriptionRef.current) {
      // Get the actual height of the description element in pixels
      const descriptionHeight = descriptionRef.current.offsetHeight;
      // Set a threshold for "long" description: 30% of the viewport height
      const threshold = window.innerHeight * 0.3;
      // Update state: true if description height exceeds the threshold, false otherwise
      setisDescriptionLong(descriptionHeight > threshold);
    }
  };

  React.useLayoutEffect(() => {
    checkDescriptionHeight();
    window.addEventListener('resize', checkDescriptionHeight);
    return () => window.removeEventListener('resize', checkDescriptionHeight);
  }, [description]);

  return (
    <div
      className={clsx('flex flex-col justify-between w-full ', {
        'ml-5': isPreview,
      })}
    >
      <div>
        {isLoggedInUser ? (
          <div
            className={clsx('relative', {
              '-ml-6': isPreview,
            })}
          >
            <BrandingHeader
              onExit={onExit}
              isActualCheckout={isActualCheckout}
              portalConfig={portalConfig}
            />
          </div>
        ) : (
          <div className="flex items-center gap-2 overflow-hidden">
            <Avatar
              size="lg"
              text="Logo"
              variant="circle"
              src={logo}
              className="group-hover:!h-0 group-hover:!w-0 w-8 h-8 transition-all duration-300"
              fallbackColor="transparent"
            />
            <Heading className="text-black-heading text-[16px] group-hover:hidden">
              {portalName}
            </Heading>
          </div>
        )}
        <div className="flex flex-col gap-8 w-full  @7xl:max-w-[448px] mt-8">
          <div>
            <div className="flex justify-between items-center flex-wrap gap-1 w-full">
              <Heading
                className={clsx(' text-black-heading', {
                  'text-[16px]': isPreview,
                  'text-2xl': !isPreview,
                })}
              >
                {name || ''}
              </Heading>
              <Heading
                className={clsx(' text-gray-small', {
                  'text-[16px]': isPreview,
                  'text-2xl': !isPreview,
                })}
              >
                {`${StripeCurrencyFormatterAdapter({
                  value: amountDue + fees,
                  currency: currency,
                })} ${interval ? formatBillingInterval(interval) : ''}`}
              </Heading>
            </div>
            {Number(totalLineItemSummary.totalAmount) > 1 && (
              <div className="flex items-center ">
                <Heading className="text-gray-small text-[12px]">
                  {`Qty: ${totalLineItemSummary.totalQuantity},`}
                </Heading>
                &nbsp;
                <Body className="text-gray-small text-[12px]">
                  {`${StripeCurrencyFormatterAdapter({
                    value:
                      Number(amountDue) /
                      Number(totalLineItemSummary.totalAmount),
                    currency: Currencies.USD,
                  })} each`}
                </Body>
              </div>
            )}
          </div>
          {coverUrl && (
            <>
              {isImageLoading && (
                <Skeleton
                  variant="rect"
                  className="w-full h-[340px] rounded-lg"
                />
              )}
              <img
                src={coverUrl}
                alt={`Product image ${coverUrl}`}
                className={clsx('w-full rounded-lg', {
                  'h-[224px] max-w-[224px]': isDescriptionLong,
                  '@5xl:max-h-[448px] 7xl:max-w-[448px]': !isDescriptionLong,
                  hidden: isImageLoading,
                  block: !isImageLoading,
                })}
                onLoad={() => setImageLoadingStatus(false)}
              />
            </>
          )}
          <div className="relative w-full">
            <RichTextStyles>
              <div
                ref={descriptionRef}
                dangerouslySetInnerHTML={{ __html: description || '' }}
                className={clsx('text-black-heading', {
                  'mb-3': isPreview,
                })}
                style={{
                  wordBreak: 'break-word',
                }}
              />
            </RichTextStyles>
            {isDescriptionLong && (
              <div
                className={clsx(
                  ' left-0 right-0 h-[100px] pointer-events-none',
                  {
                    'hidden @sm:block sticky': isPreview,
                  },
                  {
                    'fixed lg:sticky bottom-[50px] lg:bottom-[80px]':
                      !isPreview,
                  },
                )}
                style={{
                  background:
                    'linear-gradient(to top, rgba(248,249,251,1) 0%, rgba(248,249,251,0.95) 20%, rgba(248,249,251,0.85) 40%, rgba(248,249,251,0.6) 60%, rgba(248,249,251,0.3) 80%, rgba(248,249,251,0) 100%)',
                }}
              />
            )}
          </div>
        </div>
      </div>
      <div
        className={clsx(
          ' z-20 bottom-0 left-0 w-full flex lg:pt-0 fixed md:sticky bg-white lg:bg-[#F8F9FB] [&>div]:mt-0 [&>div]:py-0 lg:[&>div]:mt-6 h-16 md:h-20  justify-center items-center lg:items-start lg:justify-start',
          {
            'hidden @sm:flex': isPreview,
          },
        )}
      >
        <PoweredByAssemblyBadge isAuthScreen={isLoggedInUser ? true : false} />
      </div>
    </div>
  );
};
